import { cn } from '@invent/wl-ui-kit-next';
import { Alert, AlertDescription, AlertTitle, Button, Skeleton } from '@invent/wl-ui-kit-next/ui';
import { Check, ChevronDown, LoaderCircle } from 'lucide-react';
import * as React from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { FocusBar, PageHeader, useDocumentTitle } from '@/design';
import type {
  ICursor,
  IFinding,
  IProgress,
  IResponseState,
  ISnapshotSection
} from '../api/contracts';
import { formatDateTime } from '../format';
import { PORTAL_PATHS, receiptPath } from '../paths';
import {
  ConfirmAllDialog,
  FindingsPanel,
  ResponseSectionView,
  SectionStepper,
  StepFindingsDialog,
  addressKey,
  addressOf,
  focusFinding,
  confirmItems,
  indexAnswers,
  indexFindings,
  isAddressDrawn,
  isSectionVisible,
  numericDay,
  refusalWords,
  responseLevelFindings,
  sectionUsageCodes,
  stepBlockers,
  stepUnsaveable,
  useResponse,
  visibleFindings,
  type IAnswerAddress,
  type IFocusTarget,
  type ISectionView
} from '../runtime';
import { surveyTitle } from '../survey-labels';
import { SurveyStatus, useResponseListing, type IResponseListing } from './survey-status';

/**
 * One response, filled against survey-api.
 *
 * Sections are the steps — the definition has no notion of pages — and which of
 * them are shown, which questions inside them are visible, what is required and
 * what is wrong all come back with the state document on every write. Nothing on
 * this page re-derives any of it.
 *
 * **Laid out one task at a time** (the two-portals design, §3.3), and only laid out: the
 * portal's bar gives way to the survey's own (`FocusBar` — its name, whose it is, the save
 * state and "Finish later"); the sections run down the left, the form sits in a reading column
 * with its actions in a bar that stays under it, and what stands between the respondent and
 * submitting sits in a column on the right — folded into a strip above the form below 1440 px,
 * and the section list into a disclosure below 1024 px. Every name, role, `data-*` hook and DOM
 * order the suites drive is the one the page had before (§7).
 */
export function ResponseFormPage() {
  const { responseId } = useParams<{ responseId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const runner = useResponse(responseId ?? null);
  const listing = useResponseListing(responseId ?? null);
  const [stepIndex, setStepIndex] = React.useState(0);
  // The step's hard `step` findings and unsaveable entries a press forward met (B7-1, D8), or the
  // unsaveable entries a submit met anywhere — kept once the dialog shuts, so its closing still
  // knows which field to focus.
  const [blockers, setBlockers] = React.useState<{
    open: boolean;
    findings: IFinding[];
    unsaveable: IAnswerAddress[];
    where: 'step' | 'survey';
  }>({ open: false, findings: [], unsaveable: [], where: 'step' });
  /** A field to focus once the step it sits on is drawn (`goTo`). */
  const pendingFocus = React.useRef<Nullable<IFocusTarget>>(null);
  /** "Confirm all on this step" asked, its dialog open (D6). */
  const [confirming, setConfirming] = React.useState(false);
  /** A press forward waiting on its flush, so a second press does not ask twice. */
  const leaving = React.useRef(false);
  /** Counts the steps shown, so a press forward that waited can tell a move made meanwhile. */
  const moves = React.useRef(0);
  /** The step's heading, which a move to another step focuses unless it leads to a field. */
  const heading = React.useRef<HTMLHeadingElement>(null);
  const toHeading = React.useRef(false);
  /** The bar under the form, kept clear of the field a Tab lands on. */
  const [bar, setBar] = React.useState<Nullable<HTMLDivElement>>(null);

  /**
   * Editing an already-submitted response is a deliberate act, so it is asked for
   * in the URL rather than assumed. A query flag and not router state: it has to
   * survive a reload and a bookmark, or a refresh mid-correction would bounce the
   * respondent back to the receipt.
   */
  const isCorrecting = searchParams.get('edit') === '1';

  const { definition, view, state, render } = runner;

  const sections = React.useMemo(
    () =>
      view && render ? view.sections.filter((section) => isSectionVisible(section, render)) : [],
    [render, view]
  );
  // Where each usage stands in the form, for a report whose lines run in the form's order.
  const formOrder = React.useMemo(() => {
    const at = new Map<string, number>();
    for (const section of view?.sections ?? []) {
      for (const code of sectionUsageCodes(section)) at.set(code, at.size);
    }
    return at;
  }, [view]);

  // Resume where the respondent left off. The cursor is the app's own opaque
  // jsonb, so its shape is this file's business and nobody else's.
  const cursorApplied = React.useRef(false);
  React.useEffect(() => {
    if (cursorApplied.current || sections.length === 0 || !state) return;
    cursorApplied.current = true;

    const cursor = state.cursor as ICursor | null;
    const resumeAt = sections.findIndex(
      (section) => section.section.code === cursor?.sectionCode
    );
    if (resumeAt >= 0) setStepIndex(resumeAt);
  }, [sections, state]);

  const answers = React.useMemo(() => indexAnswers(state?.answers ?? []), [state]);
  // What a forward press revealed: every usage the pressed steps draw.
  const revealedUsages = React.useMemo(() => {
    const codes = new Set<string>();
    for (const section of sections) {
      if (!runner.revealedSteps.has(section.section.code)) continue;
      for (const code of sectionUsageCodes(section)) codes.add(code);
    }
    return codes;
  }, [runner.revealedSteps, sections]);
  const shownFindings = React.useMemo(
    () =>
      visibleFindings(state?.findings ?? [], {
        reveal: runner.revealFindings,
        touched: runner.touched,
        revealedUsages,
        parentOf: (code) => view?.usagesByCode.get(code)?.parentUsage ?? null
      }),
    [revealedUsages, runner.revealFindings, runner.touched, state, view]
  );
  const findings = React.useMemo(() => indexFindings(shownFindings), [shownFindings]);
  // The unsaveable-entry hint is held back on exactly the rule `visibleFindings` holds a
  // required finding back on, and filtered here rather than inside the hook so the two
  // decisions sit together: a field still being typed into is not yet wrong, and "enter a
  // whole number" under the first character of a number being entered is precisely what
  // that rule exists to prevent. One attempted submit reveals the lot.
  const shownMalformed = React.useMemo(
    () =>
      new Set(
        [...runner.malformed].filter(
          (address) =>
            runner.revealFindings ||
            runner.touched.has(address) ||
            revealedUsages.has(addressOf(address).usage)
        )
      ),
    [revealedUsages, runner.malformed, runner.revealFindings, runner.touched]
  );
  // Findings arrive addressed by usage code; the panels name the question instead, and
  // an `engine.format` reads its respondent copy off the usage's own constraint.
  const usageOf = React.useCallback(
    (usageCode: string) => view?.usagesByCode.get(usageCode),
    [view]
  );
  const globalFindings = React.useMemo(
    () => responseLevelFindings(shownFindings),
    [shownFindings]
  );
  const stepIndexInRange = Math.min(stepIndex, Math.max(sections.length - 1, 0));
  // The proposals on the step being rendered. `pendingPrefill` is the whole response's, and
  // the panel offers a decision about what is on screen — narrowed through the section's own
  // tree, because a repeat member carries no `section` on the wire and filtering the
  // snapshot by that field would silently drop every one of them. Memoized here, above the
  // early returns a hook cannot live below, rather than recomputed on every keystroke.
  const stepPrefill = React.useMemo(() => {
    const section = sections[stepIndexInRange];
    if (!section) return [];
    const stepUsages = sectionUsageCodes(section);
    return runner.pendingPrefill.filter((address) => stepUsages.has(address.usage));
  }, [runner.pendingPrefill, sections, stepIndexInRange]);
  // The proposals survey-api holds submit on (`engine.unconfirmed_proposal`, D5), whether shown
  // yet or not, and the rest of `pendingPrefill`, which hold nothing.
  const prefillCounts = React.useMemo(() => {
    const holding = new Set(
      (state?.findings ?? [])
        .filter((finding) => finding.code === 'engine.unconfirmed_proposal' && finding.usage)
        .map((finding) => addressKey(finding.usage!, finding.instanceId, finding.instanceKey))
    );
    const others = runner.pendingPrefill.filter(
      (address) => !holding.has(addressKey(address.usage, address.instanceId, address.instanceKey))
    );
    return { toConfirm: holding.size, unconfirmed: others.length };
  }, [runner.pendingPrefill, state]);

  const surveyName = definition ? `${surveyTitle(definition.surveyCode)} survey` : null;
  useDocumentTitle(surveyName);

  // The field `goTo` moved to a step for, focused once that step is drawn.
  React.useEffect(() => {
    const target = pendingFocus.current;
    if (!target) return;
    pendingFocus.current = null;
    focusFinding(target, usageOf);
  }, [stepIndexInRange, usageOf]);

  // A step moved to is read from its top: the next Tab leaves the heading, not the button pressed.
  React.useEffect(() => {
    if (!toHeading.current) return;
    toHeading.current = false;
    heading.current?.focus({ preventScroll: true });
  }, [stepIndexInRange]);

  // The page scrolls a focused field clear of the sticky bar under the form, not under it.
  React.useEffect(() => {
    if (!bar) return;
    const page = document.documentElement;
    const observer = new ResizeObserver(() => {
      page.style.scrollPaddingBottom = `${bar.offsetHeight}px`;
    });
    observer.observe(bar);
    return () => {
      observer.disconnect();
      page.style.scrollPaddingBottom = '';
    };
  }, [bar]);

  if (!responseId) return <Navigate to={PORTAL_PATHS.surveys} replace />;

  // Home opens at its top, and says the answers are saved only when nothing is left unsaved: the
  // last flush landed, and no refused, held or shut-out answer is on screen — a flush clears none
  // of those, and with nothing queued it answers the state as it stands.
  const finishLater = async () => {
    const unsaved =
      runner.rejections.length > 0 || runner.malformed.size > 0 || runner.cycleClosed;
    const landed = await runner.flush();
    window.scrollTo({ top: 0 });
    navigate(PORTAL_PATHS.home, { state: { saved: landed !== null && !unsaved } });
  };

  if (runner.status === 'loading') {
    return (
      <>
        <FocusBar>
          <SurveyBarTitle surveyName={null} asOf={null} listing={listing} />
        </FocusBar>
        <SurveyGrid
          steps={<Skeleton className="h-72 w-full" />}
          status={<Skeleton className="h-40 w-full" />}
          form={
            <div className="grid gap-4">
              <Skeleton className="h-10 w-2/3" />
              <Skeleton className="h-40 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
          }
        />
      </>
    );
  }

  if (runner.status === 'missing') {
    return (
      <>
        <PageHeader title="Survey not found" />
        <div className="mx-auto w-full max-w-[1120px] px-6 py-8 lg:px-10">
          <Alert>
            <AlertTitle>We could not open that survey</AlertTitle>
            <AlertDescription>
              It may belong to another firm, or it may have been closed. Everything you can reach
              is on your surveys page.
            </AlertDescription>
          </Alert>
          <Button className="mt-6" onClick={() => navigate(PORTAL_PATHS.surveys)}>
            Back to your surveys
          </Button>
        </div>
      </>
    );
  }

  if (!definition || !state || !view || sections.length === 0) {
    return (
      <>
        <PageHeader title="Survey unavailable" />
        <div className="mx-auto w-full max-w-[1120px] px-6 py-8 lg:px-10">
          <Alert variant="destructive">
            <AlertTitle>Nothing to show</AlertTitle>
            <AlertDescription>{runner.error ?? 'This survey has no visible sections.'}</AlertDescription>
          </Alert>
        </div>
      </>
    );
  }

  // A submitted survey opens on its receipt. The cursor points at where the
  // respondent was *filling*, and dropping them back there says nothing about the
  // survey already being in — it reads as unfinished work. The receipt says it is
  // in and offers the way back to correct it.
  if (state.status === 'submitted' && !isCorrecting) {
    return <Navigate to={receiptPath(responseId)} replace />;
  }

  const step = sections[stepIndexInRange];
  const intro = sectionIntro(step.section);
  const isLastStep = stepIndex >= sections.length - 1;
  // `submitted` is still writable: a respondent may correct and re-submit until
  // the cycle closes and the response is locked.
  //
  // The cycle's own close is the second half of that sentence and does not travel on the
  // response: SR-213 refuses every write in a closed cycle while the response it refuses
  // them for is still an ordinary `draft`. `runner.cycleClosed` is that refusal, seen once
  // and remembered, and it is what keeps the form from offering fields nothing will store.
  // `runner.withdrawn` is the same switch for a response staff took out of play (SR-470): its
  // state on screen is the last one read, and every route now answers 404.
  const readOnly =
    runner.cycleClosed ||
    runner.withdrawn !== null ||
    (state.status !== 'draft' && state.status !== 'submitted');

  // At the top at once: scrolled smoothly, the next step was drawn where the last one ended and
  // slid up under the respondent for most of a second.
  const showStep = (index: number) => {
    moves.current += 1;
    // Only for a move that changes the step: one left pending would fire on a later clamp.
    toHeading.current = pendingFocus.current === null && index !== stepIndexInRange;
    setStepIndex(index);
    window.scrollTo({ top: 0 });
    runner.saveCursor({ sectionCode: sections[index].section.code });
  };

  // A stepper button, "Back" and every move on a form nothing can change are free (D4): the
  // answers are saved either way, and no answer could clear what the dialog would ask for.
  // "Save and continue" reveals its step's findings (`visibleFindings`), waits for the flush and
  // reads the step's hard `step` findings off the state it landed at (`stepBlockers`): any, and
  // the step stays with the dialog saying why (B7-1, the owner's Q3 (a)). A flush that did not
  // land keeps the step too, its banner saying why; and a move made while it was out — a stepper
  // button — is the one kept.
  const moveTo = (index: number) => {
    showStep(index);
    void runner.flush();
  };

  // Whether a step draws a usage: a finding about one leads there (D9).
  const drawn = (usage: string) =>
    sections.some((section) => sectionUsageCodes(section).has(usage));

  // Focuses a field, showing the step it sits on first when that is not the step on screen.
  const goTo = (target: IFocusTarget) => {
    const at = target.usage
      ? sections.findIndex((section) => sectionUsageCodes(section).has(target.usage!))
      : -1;
    if (at < 0 || at === stepIndexInRange) {
      focusFinding(target, usageOf);
      return;
    }
    pendingFocus.current = target;
    showStep(at);
  };

  const continueFrom = async (index: number) => {
    if (readOnly) {
      moveTo(index);
      return;
    }
    runner.revealStep(sections[stepIndexInRange].section.code);
    if (leaving.current) return;
    leaving.current = true;
    const pressedAt = moves.current;
    try {
      const landed = await runner.flush();
      if (!landed || moves.current !== pressedAt) return;
      const found = stepBlockers(sections[stepIndexInRange], landed.findings);
      const unsaveable = stepUnsaveable(sections[stepIndexInRange], runner.malformed, (address) =>
        isAddressDrawn(address, landed, view!)
      );
      if (found.length > 0 || unsaveable.length > 0) {
        setBlockers({ open: true, findings: found, unsaveable, where: 'step' });
        return;
      }
      showStep(index);
    } finally {
      leaving.current = false;
    }
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!isLastStep) {
      await continueFrom(stepIndex + 1);
      return;
    }

    // Text no payload can carry was never sent, so survey-api's submit would judge the answer
    // without it (D8): said first, on whichever step it sits.
    const unsaveable = sections.flatMap((section) =>
      stepUnsaveable(section, runner.malformed, (address) => isAddressDrawn(address, state!, view!))
    );
    if (unsaveable.length > 0) {
      setBlockers({ open: true, findings: [], unsaveable, where: 'survey' });
      return;
    }

    if (await runner.submit()) {
      navigate(receiptPath(responseId));
      return;
    }
    // "Submission is blocked" opens at the top of the form column, and the button that asked
    // is at its foot.
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <FocusBar>
        <SurveyBarTitle
          surveyName={surveyName}
          asOf={definition.cycle.config.as_of_date ?? null}
          listing={listing}
        />
        <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-4">
          <SaveState
            isSaving={runner.isSaving}
            hasPendingWrites={runner.hasPendingWrites}
            lastSavedAt={runner.lastSavedAt}
          />
          <Button type="button" variant="outline" size="sm" onClick={() => void finishLater()}>
            Finish later
          </Button>
        </div>
      </FocusBar>

      <SurveyGrid
        steps={
          <SectionList
            sections={sections}
            currentIndex={stepIndex}
            findings={shownFindings}
            progress={state.progress?.sections}
            onSelect={moveTo}
          />
        }
        status={
          <SurveyStatus
            findings={shownFindings}
            globalFindings={globalFindings}
            progress={state.progress}
            toConfirm={readOnly ? 0 : prefillCounts.toConfirm}
            unconfirmed={readOnly ? 0 : prefillCounts.unconfirmed}
            closesAt={listing?.closesAt ?? null}
            usageOf={usageOf}
            onAcknowledge={runner.acknowledge}
          />
        }
        form={
          <>
            {runner.submitReport && (
              <FindingsPanel
                findings={runner.submitReport}
                title="Submission is blocked"
                description="Fix the items below — or acknowledge the warnings — and submit again."
                usageOf={usageOf}
                placeOf={(code) => formOrder.get(code) ?? formOrder.size}
                yearLabels={definition.cycle.config.year_labels}
                onAcknowledge={runner.acknowledge}
                onGoTo={goTo}
                drawn={drawn}
                className="mt-0 mb-6"
              />
            )}

            <h1
              ref={heading}
              tabIndex={-1}
              className="text-[2rem] leading-tight font-light tracking-[-0.01em] break-words text-[var(--color-royal-blue)] outline-none sm:text-[2.25rem]"
            >
              {step.section.title}
            </h1>

            {intro && (
              // survey-api's words, its tokens already expanded (the cycle's as-of date), and plain
              // text: no intro carries markup, so line breaks are the one shape kept (B7-2).
              <p
                data-section-intro
                className="mt-3 max-w-[40rem] text-[0.9375rem] leading-relaxed whitespace-pre-line text-muted-foreground"
              >
                {intro}
              </p>
            )}

            {/* Not over a response taken out of play: it can no longer be corrected (SR-470, the
                owner's Q9). */}
            {state.status === 'submitted' && !runner.withdrawn && (
              <Alert className="mt-6">
                <AlertTitle>Already submitted</AlertTitle>
                <AlertDescription>
                  This survey is in. You can still correct it and submit again — every submission
                  is kept, and research works from the latest.
                </AlertDescription>
              </Alert>
            )}

            {readOnly && (
              <Alert className="mt-6" data-withdrawn={runner.withdrawn ? '' : undefined}>
                <AlertTitle>Closed to changes</AlertTitle>
                <AlertDescription>
                  {closedSentence(runner.withdrawn, runner.cycleClosed, state.status)}
                </AlertDescription>
                {runner.withdrawn && (
                  <Button
                    type="button"
                    size="sm"
                    className="mt-3 w-fit"
                    onClick={() => navigate(PORTAL_PATHS.surveys)}
                  >
                    Back to your surveys
                  </Button>
                )}
              </Alert>
            )}

            {runner.error && (
              <Alert variant="destructive" className="mt-6">
                <AlertTitle>Something needs attention</AlertTitle>
                <AlertDescription>{runner.error}</AlertDescription>
              </Alert>
            )}

            {runner.rejections.length > 0 && (
              <Alert variant="destructive" className="mt-6">
                <AlertTitle>These answers were refused</AlertTitle>
                <AlertDescription>
                  <ul className="list-inside list-disc">
                    {runner.rejections.map((rejection) => {
                      const usage = usageOf(rejection.usage);
                      return (
                        <li key={`${rejection.usage}-${rejection.instanceKey}`}>
                          {usage ? `${usage.label} (${rejection.usage})` : rejection.usage}:{' '}
                          {refusalWords(rejection.reason)}
                        </li>
                      );
                    })}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {stepPrefill.length > 0 && !readOnly && (
              // One request for the whole step, not one per field: a returning FA
              // participant meets 77 proposals, and each single confirmation costs an
              // evaluation pass and a whole state document. The count is data as well as
              // copy — a spec asserting on the sentence would be asserting on English.
              <div
                data-prefill-pending={stepPrefill.length}
                className="mt-6 flex flex-wrap items-center justify-between gap-x-5 gap-y-3 rounded-[0.625rem] bg-[color-mix(in_srgb,var(--color-sapphire)_8%,white)] px-5 py-4"
              >
                <p className="max-w-[34rem] text-[0.9375rem] leading-relaxed">
                  <span className="font-medium text-[var(--color-royal-blue)]">
                    {stepPrefill.length === 1
                      ? 'One answer here is already filled in.'
                      : `${stepPrefill.length} answers here are already filled in.`}
                  </span>{' '}
                  Each says where it came from. Check them one by one, or confirm them all if
                  nothing has changed.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="bg-white"
                  onClick={() => setConfirming(true)}
                >
                  Confirm all on this step
                </Button>
              </div>
            )}

            <ConfirmAllDialog
              open={confirming}
              items={confirmItems(
                stepPrefill,
                answers,
                view,
                state.repeats,
                definition.cycle.config.year_labels,
                { drafts: runner.drafts, malformed: runner.malformed }
              )}
              onConfirm={(addresses) => {
                setConfirming(false);
                void runner.confirmPrefill(addresses, 'confirm');
              }}
              onClose={() => setConfirming(false)}
            />

            <StepFindingsDialog
              open={blockers.open}
              findings={blockers.findings}
              unsaveable={blockers.unsaveable}
              where={blockers.where}
              onFocus={goTo}
              usageOf={usageOf}
              yearLabels={definition.cycle.config.year_labels}
              onClose={() => setBlockers((shown) => ({ ...shown, open: false }))}
            />

            <form className="mt-6" noValidate onSubmit={onSubmit} onKeyDown={keepEnterInBox}>
              <ResponseSectionView
                view={step}
                state={state}
                render={render!}
                yearLabels={definition.cycle.config.year_labels}
                drafts={runner.drafts}
                malformed={shownMalformed}
                answers={answers}
                findings={findings}
                clearedPrefill={runner.clearedPrefill}
                readOnly={readOnly}
                onChange={runner.setValue}
                onCommit={() => void runner.commit()}
                onConfirm={(address, action) => void runner.confirmPrefill([address], action)}
                onDecide={(addresses, action) => void runner.confirmPrefill(addresses, action)}
                onUnlink={(instanceId, names) => void runner.unlinkMember(instanceId, names)}
                lookups={runner.lookups}
                onAddRow={runner.addRow}
                onRemoveRow={(instanceId) => void runner.removeRow(instanceId)}
              />

              {/* Stays under the form while it scrolls, so the way on is never a scroll away. */}
              <div
                ref={setBar}
                className="sticky bottom-0 z-10 mt-6 flex flex-wrap items-center gap-3 border-t border-border bg-[color-mix(in_srgb,var(--color-off-white)_92%,transparent)] py-4 backdrop-blur-sm"
              >
                {stepIndex > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    className="bg-white"
                    onClick={() => moveTo(stepIndex - 1)}
                  >
                    Back
                  </Button>
                )}

                <Button type="submit" disabled={runner.isSubmitting || readOnly}>
                  {isLastStep
                    ? runner.isSubmitting
                      ? 'Submitting…'
                      : state.status === 'submitted'
                        ? 'Submit again'
                        : 'Submit survey'
                    : 'Save and continue'}
                </Button>

                {/* `state.revision` is deliberately not shown. It is the
                    optimistic-concurrency token — it counts writes, so it climbs
                    once per saved field and reads as a version number the
                    respondent has reached. What they actually want to know is
                    whether their work is saved, and that is the bar above. */}
                <span className="ml-auto text-sm text-muted-foreground tabular-nums">
                  Section {stepIndex + 1} of {sections.length}
                </span>
              </div>
            </form>
          </>
        }
      />
    </>
  );
}

/** Why a read-only form is closed: taken out of play first, then a closed cycle, then a status that
 *  takes no writes. */
function closedSentence(
  withdrawn: Nullable<{ after: string }>,
  cycleClosed: boolean,
  status: IResponseState['status']
): string {
  if (withdrawn) {
    return (
      'This survey was taken out of play by Veritant. What you typed after ' +
      `${formatDateTime(withdrawn.after)} was not saved.`
    );
  }
  if (cycleClosed) return 'This cycle has closed, so this response can be read but not edited.';
  return `This response is ${status}, so it can be read but not edited.`;
}

/**
 * Enter in a field stays in the field (the survey review plan's D7, G14): a browser submits a
 * form on Enter in its inputs — a radio and a checkbox too, in Chromium — which here would move
 * the step on, or submit the survey, from a key meant for the field. Only an `input` of the
 * form's own DOM: a button, a textarea and a Radix option, portaled out of the DOM but not out of
 * React's tree, still take Enter.
 */
function keepEnterInBox(event: React.KeyboardEvent<HTMLFormElement>) {
  const target = event.target;
  if (event.key !== 'Enter' || !(target instanceof HTMLInputElement)) return;
  if (!event.currentTarget.contains(target)) return;
  if (BUTTON_INPUTS.has(target.type)) return;
  event.preventDefault();
}

/** The `input` types that are buttons: Enter on one is the press it means. */
const BUTTON_INPUTS = new Set(['button', 'reset', 'submit', 'file', 'image']);

/** A section's intro (SR-416, `SectionIntro.cs`): its `default`, or null. No variant is authored,
 *  and the selector that would pick one is unspecified, so none is read. */
function sectionIntro(section: ISnapshotSection): Nullable<string> {
  const text = section.intro?.default;
  return typeof text === 'string' && text.trim() !== '' ? text : null;
}

/**
 * The three columns, placed by grid area so each keeps its place in the document whatever the
 * width: the section list, the form, then the status — the order the suites read the page in.
 * Below 1440 px the status becomes a strip above the form; below 1024 px everything is one column,
 * the section list first.
 */
function SurveyGrid({
  steps,
  form,
  status
}: {
  steps: React.ReactNode;
  form: React.ReactNode;
  status: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'mx-auto grid w-full max-w-[1440px] gap-x-12 gap-y-6 px-6 pt-8 lg:px-10 lg:pt-9',
        "grid-cols-1 [grid-template-areas:'steps'_'status'_'form']",
        "lg:grid-cols-[15rem_minmax(0,45rem)] lg:grid-rows-[auto_1fr] lg:justify-center lg:[grid-template-areas:'steps_status'_'steps_form']",
        "min-[90rem]:grid-cols-[17rem_minmax(0,45rem)_18.75rem] min-[90rem]:grid-rows-1 min-[90rem]:[grid-template-areas:'steps_form_status']"
      )}
    >
      <div className="min-w-0 [grid-area:steps]">{steps}</div>
      <div className="min-w-0 pb-10 [grid-area:form]">{form}</div>
      <div className="min-w-0 [grid-area:status]">{status}</div>
    </div>
  );
}

/**
 * The section list — a column that stays in view from 1024 px, and below that a disclosure
 * under a button that names the step by its place alone: a button whose name held a section's
 * title would be a second control answering to it.
 */
function SectionList({
  sections,
  currentIndex,
  findings,
  progress,
  onSelect
}: {
  sections: ISectionView[];
  currentIndex: number;
  findings: IFinding[];
  progress: IProgress['sections'] | undefined;
  onSelect: (index: number) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const listId = React.useId();

  return (
    <nav aria-label="Sections" className="lg:sticky lg:top-[5.5rem]">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((shown) => !shown)}
        className="flex h-11 w-full items-center justify-between rounded-lg border border-border bg-white px-4 text-[0.9375rem] text-[var(--color-royal-blue)] outline-none focus-visible:ring-2 focus-visible:ring-ring lg:hidden"
      >
        <span>
          Section {currentIndex + 1} of {sections.length}
          <span className="text-muted-foreground"> · all sections</span>
        </span>
        <ChevronDown
          className={cn('size-4 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </button>
      <p className="mb-3.5 ml-3 hidden text-[0.84375rem] text-muted-foreground lg:block">
        {sections.length === 1 ? '1 section' : `${sections.length} sections`}
      </p>
      <div id={listId} className={cn('mt-2 lg:mt-0 lg:block', !open && 'hidden')}>
        <SectionStepper
          sections={sections}
          currentIndex={currentIndex}
          findings={findings}
          progress={progress}
          onSelect={(index) => {
            setOpen(false);
            onSelect(index);
          }}
        />
      </div>
    </nav>
  );
}

/**
 * The survey's name, the day its figures are as of and whose it is, in the bar — the last once the
 * catalogue says. The day, not the cycle's code: a code is not a year (the survey review's §5).
 */
function SurveyBarTitle({
  surveyName,
  asOf,
  listing
}: {
  surveyName: Nullable<string>;
  /** The cycle's `as_of_date`, ISO; said as survey-api's labels say it (`12/31/2026`). */
  asOf: Nullable<string>;
  listing: Nullable<IResponseListing>;
}) {
  if (!surveyName) return <span className="min-w-0 flex-1" />;
  return (
    <p className="hidden min-w-0 flex-1 truncate text-[0.9375rem] text-muted-foreground md:block">
      <span className="font-medium text-[var(--color-royal-blue)]">{surveyName}</span>
      {asOf && `, data as of ${numericDay(asOf)}`}
      {listing && `, for ${listing.participantName}`}
    </p>
  );
}

/**
 * The page's one `[data-save-state]`, moved into the bar rather than duplicated. The three
 * states are data as well as copy: a spec asserting on "Saved" would be asserting on a
 * timestamp's formatting, and on English.
 */
function SaveState({
  isSaving,
  hasPendingWrites,
  lastSavedAt
}: {
  isSaving: boolean;
  hasPendingWrites: boolean;
  lastSavedAt: Nullable<string>;
}) {
  const saved = !isSaving && !hasPendingWrites;
  // On a phone the bar holds the mark, this and "Finish later", so the words step back to a
  // screen reader behind an icon; the time is for a screen wide enough to spare it.
  return (
    <p
      className={cn(
        'flex items-center gap-1.5 text-sm whitespace-nowrap',
        saved ? 'text-[var(--color-teal)]' : 'text-muted-foreground'
      )}
      data-save-state={isSaving ? 'saving' : hasPendingWrites ? 'pending' : 'saved'}
    >
      {saved ? (
        <Check className="size-4" aria-hidden />
      ) : (
        <LoaderCircle className={cn('size-4', isSaving && 'animate-spin')} aria-hidden />
      )}
      <span className="max-sm:sr-only">
        {isSaving ? 'Saving…' : hasPendingWrites ? 'Unsaved changes' : 'Saved'}
        {saved && lastSavedAt && (
          <span className="hidden lg:inline"> {formatDateTime(lastSavedAt)}</span>
        )}
      </span>
    </p>
  );
}
